import { track } from '@sloyd/analytics';
import * as api from '@sloyd/api';
import { actions, selectors } from '@sloyd/store-base';
import '@sloyd/types';
import { prompt, toast } from '@sloyd/ui';
import { bridge } from '@sloyd/viewer';
import { call, delay, fork, put, select, takeEvery } from 'saga-ts';
import { guid4, invokeEvent, l } from 'shared-base';
import ContentGoldenDetailsContainer from '../components/ContentGoldenDetails/ContentGoldenDetails.container';
import { nudgeGolden } from './helpers/golden';
import { dataGuard } from './helpers/guards';
import { getIdAndName } from './helpers/idAndName';

export const saga = {
  id: 'sloyd.golden',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['GOLDEN'],
  },
};

type Verb =
  | 'bootstrap' //
  | 'fetchAll' //
  | 'manage' //
  | 'patch'
  | 'next'
  | 'prev'
  | 'load'
  | 'save';

type Action = {
  type: 'GOLDEN';
  verb: Verb;
  id: string;
  payload: Json;
};

const map: Record<Verb, any> = {
  bootstrap: bootstrap,
  load: loadGolden,
  save: saveGolden,
  fetchAll: fetchAll,
  manage: manageGolden,
  patch: patchGolden,
  next: nextGolden,
  prev: prevGolden,
};

const analyitcsMapModels: any = {
  load: 'Load Golden',
  bootstrap: 'Bootstrap Golden',
  save: 'Save Golden',
  fetchAll: 'Fetch All Golden',
  patch: 'Patch Golden',
  manage: 'Manage Golden',
  next: 'Next Golden',
  prev: 'Prev Golden',
};

export function* nextGolden(action: Action, _model: IModel) {
  try {
    yield fork(nudgeGolden, 1);
  } catch (err) {}
}

export function* prevGolden(action: Action, _model: IModel) {
  try {
    yield fork(nudgeGolden, -1);
  } catch (err) {}
}

export function* manageGolden(action: Action, _model: IModel) {
  const { id } = action;

  yield put(actions.currentIds.patch({ goldenId: id }));
  yield delay(10);

  try {
    yield prompt.custom({
      title: 'Golden Details',
      component: ContentGoldenDetailsContainer,
    });
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* fetchAll(_action: Action, _model: IModel) {
  try {
    yield put(actions.goldens.setAll({}));
    const res = yield api.golden.getAll();

    if (!res.isSuccess) return;
    yield put(actions.goldens.setAll(res.data));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* patchGolden(action: Action, model: IModel) {
  const { payload } = action;
  try {
    const goldenId = yield* select(selectors.single.$currentGoldenId);
    const resPatch = yield api.golden.patch(goldenId, payload);
    yield put(actions.goldens.patch(goldenId, payload));

    if (resPatch.isSuccess) {
      // toast.show('Golden updated');
    }
  } catch (err) {}
}

export function* bootstrap(action: Action, model: IModel) {
  try {
    const generatorId = yield* select(selectors.single.$currentGeneratorId);
    yield put(actions.goldens.setAll({}));
    const res = yield api.golden.get(generatorId);
    if (!res.isSuccess) return;
    yield put(actions.goldens.setAll(res.data));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* loadGolden(action: Action, model: IModel) {
  try {
    const { payload } = action;
    const { index } = payload;

    const goldens = yield select(selectors.base.$goldens);
    const golden = goldens[index];
    if (!golden) return;
    bridge.replaceModel('main', golden.model);
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* saveGolden(action: Action, model: IModel) {
  try {
    const { payload } = action;
    const { sequence } = payload ?? {};

    const model = yield* select(selectors.single.$currentModel);
    if (!model) return;

    const id = guid4();

    const golden = {
      id,
      sequence,
      generatorId: model.generatorId,
      model,
    };

    const res = yield api.golden.add(golden);

    if (!res.isSuccess) return;
    toast.show('Golden saved');

    yield put(actions.goldens.set(id, golden));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* golden(action: any) {
  const { verb, id } = action;
  yield delay(10);

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  l(`saga|saga.golden ${verb} on ${id}`);

  console.time('dataGuard');

  yield* dataGuard();

  console.timeEnd('dataGuard');

  const model = yield* select(selectors.single.$model, id);
  const idAndName = yield* call(getIdAndName, { model });
  track(analyitcsMapModels[verb], { origin, ...idAndName });

  yield* fork(saga, action, model);
}

export function* root() {
  yield takeEvery('GOLDEN', golden);
}
